import React from 'react';
import PropTypes from 'prop-types';
import DrawerPanelBody from './DrawerPanelBody';
import { SSRDrilldownLinks } from './SSRDrilldownLinks';

/**
 * SSRNavPanel — static HTML fallback rendered on the server before React
 * hydrates on the client.
 *
 * Purpose: SEO / crawler visibility. Search engine crawlers and SSR renderers
 * see this markup instead of the interactive Modal, ensuring all nav links
 * are discoverable in the initial HTML payload. Once the client mounts,
 * NavigationDrawer replaces this with the fully interactive MainPanel.
 *
 * The panel is intentionally hidden from users and assistive technology
 * (visibility: hidden + aria-hidden + inert) to prevent double interaction.
 *
 * Carries the same DS classes as `SlidePanel` (`modal dock-full
 * ws-nav-drawer-modal`) so the host page styles it identically — see the Host DS
 * contract in CLAUDE.md.
 *
 * @param {Object} props
 * @param {string} props.dockClass - 'dock-left' or 'dock-right'
 * @param {string} [props.title]
 * @param {React.RefObject} [props.closeRef]
 * @param {ElementRef} [props.searchRef]
 * @returns {React.ReactElement}
 */
const SSRNavPanel = ({ dockClass, title, closeRef, searchRef }) => (
	<div
		className={`modal ${dockClass} dock-full ws-nav-drawer-modal`}
		style={{ display: 'block', visibility: 'hidden' }}
		aria-hidden="true"
		inert=""
		data-testid="ssr-nav-panel"
	>
		<div className="modal-dialog">
			<div className="modal-content">
				<div className="ws-nav-drawer__panel-inner">
					<DrawerPanelBody
						title={title}
						closeRef={closeRef}
						searchRef={searchRef}
					/>
				</div>
			</div>
		</div>
		<SSRDrilldownLinks />
	</div>
);

SSRNavPanel.propTypes = {
	dockClass: PropTypes.string.isRequired,
	title: PropTypes.string,
	closeRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
	searchRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.instanceOf(Element) })
	])
};

SSRNavPanel.defaultProps = {
	title: '',
	closeRef: null,
	searchRef: null
};

export { SSRNavPanel };
